// PRD-57 FR-14/FR-16 — SPA error capture, wired at chokepoints so no call
// site has to opt in:
//
//   1. the `logger` sink      → `app_error` / `app_warning`
//   2. two `window` listeners → `$exception` (PostHog Error Tracking)
//
// (The third chokepoint, failed API requests, lives in `api/client.ts`; the
// route error boundary and the lazy-route loader report their own `Error`s.)
//
// Everything here ends in `lib/analytics.ts`, which owns the student-surface
// gate and the "never throws" guarantee.

import { analytics } from "@lib/analytics"
import { setLogSink } from "@lib/logger"

/**
 * ALLOW-list, not a deny-list. Existing `logger` call sites pass
 * `studentId`, `student_id`, `assignmentId`, form `patch` objects and raw
 * `message` strings — a deny-list would have to anticipate every future key,
 * and the first one it missed would ship a minor's identifier to a third
 * party. Assignment ids are excluded on purpose: an assignment is one
 * student. A key not listed here is dropped silently; add it deliberately.
 */
const FORWARDED_CONTEXT_KEYS = [
  "code",
  "status",
  "reason",
  "kind",
  "category",
  "attempt",
  "route",
  "is_chunk_error",
  "recovery_mode",
  "event_type",
  "material_id",
  "classroom_id",
  "task_run_id",
] as const

const ERROR_TEXT_MAX_LENGTH = 200

/**
 * Per page load. `realtime.socket_error` and `realtime.heartbeat_timeout`
 * can fire every few seconds on school Wi-Fi; uncapped, one professor's bad
 * afternoon spends the month's quota and buries everything else. After the
 * tenth identical error the eleventh teaches nothing.
 */
const MAX_PER_EVENT: Record<"error" | "warning", number> = { error: 10, warning: 3 }
const MAX_TOTAL = 50
const MAX_EXCEPTIONS = 20

const sentPerEvent = new Map<string, number>()
let sentTotal = 0
let sentExceptions = 0

type ForwardedValue = string | number | boolean | null

function allowListed(context: Record<string, unknown> | undefined) {
  const out: Record<string, ForwardedValue> = {}
  if (!context) return out
  for (const key of FORWARDED_CONTEXT_KEYS) {
    const value = context[key]
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      out[key] = value
    }
  }
  // `error: String(err)` is the repo-wide idiom. Kept, but truncated: it is
  // an exception message, and those occasionally quote user input.
  if (typeof context.error === "string") {
    out.error = context.error.slice(0, ERROR_TEXT_MAX_LENGTH)
  }
  return out
}

function logSink(
  level: "warning" | "error",
  event: string,
  context: Record<string, unknown> | undefined,
) {
  // The analytics module reports its own failures through the logger.
  // Forwarding those back into analytics is an infinite loop.
  if (event.startsWith("analytics.")) return
  if (sentTotal >= MAX_TOTAL) return
  const key = `${level}:${event}`
  const count = sentPerEvent.get(key) ?? 0
  if (count >= MAX_PER_EVENT[level]) return
  sentPerEvent.set(key, count + 1)
  sentTotal += 1
  analytics.captureLog(level, event, allowListed(context))
}

function reportException(error: unknown, source: string) {
  if (sentExceptions >= MAX_EXCEPTIONS) return
  sentExceptions += 1
  analytics.captureException(error, { source, handled: false })
}

let installed = false

/** Call once, from `main.tsx`, before the first render. */
export function installObservability() {
  if (installed || typeof window === "undefined") return
  installed = true

  setLogSink(logSink)

  // Our own listeners rather than PostHog's exception-autocapture observer:
  // that one lazy-loads a script from an assets host the `/ingest` proxy
  // does not front, and is gated on a project-level opt-in (ADR-0046 §6).
  window.addEventListener("error", (event) => {
    // Resource load failures (<img>, <script>) arrive here with no `error`
    // and no useful stack; chunk-load failures are reported by lazyRoute.
    if (!event.error) return
    reportException(event.error, "window.error")
  })
  window.addEventListener("unhandledrejection", (event) => {
    reportException(event.reason, "unhandledrejection")
  })
}
